import { useCallback, useEffect, useState } from "react";
import {
  fetchDebtsToPayAsPropertyPendingTotal,
  fetchExpenses,
  fetchFinances,
  findMyPaysAsProperty,
} from "../../../helpers/api/finance";
import handleErrorMessage from "../../../helpers/errors/handleErrorMessage";

const TOO_MANY_REQUESTS = 429;
// Cuántos pagos por aprobar se muestran en la pestaña Cobros.
const PENDING_PAYS_PREVIEW = 5;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// El backend limita la frecuencia de peticiones y esta pantalla hace varias a
// la vez al entrar; ante un 429 se reintenta igual que hacían Finanzas y
// Contabilidad.
const withRetry = async (request, delay = 1000) => {
  try {
    return await request();
  } catch (error) {
    if (error?.response?.status === TOO_MANY_REQUESTS) {
      await wait(delay);
      return withRetry(request, delay);
    }
    throw error;
  }
};

// Tipos de gasto que no se registran desde el módulo de Finanzas (Contabilidad
// ya los dejaba fuera de su lista).
const HIDDEN_EXPENSE_TYPES = [4, 5];

// `paymented` llega como booleano; negar sirve también si llega nulo.
const isPending = (debt) => !debt.paymented;

// Lo que falta pagar, no el monto original: una cuenta con abonos parciales
// sigue pendiente, pero solo debe sumar su saldo.
export const pendingAmountOf = (debt) =>
  debt.pending_amount !== undefined && debt.pending_amount !== null
    ? Number(debt.pending_amount)
    : Number(debt.amount || 0) - Number(debt.total_paid || 0);

const initialState = {
  loading: true,
  accounts: [],
  pendingDebts: [],
  pendingPays: { items: [], total: 0, loading: true },
  receivableTotal: { value: 0, loading: true },
  expenseTypes: [],
};

const useFinanceHubData = ({ condominiumId, role, enabled }) => {
  const [state, setState] = useState(initialState);
  const [reloadKey, setReloadKey] = useState(0);
  const isManager = role === "manager";
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!enabled || !condominiumId) return undefined;

    let isMounted = true;
    const update = (patch) => isMounted && setState((prev) => ({ ...prev, ...patch }));

    const loadFinances = async () => {
      try {
        const response = await withRetry(
          () => fetchFinances({ condominiumId }),
          3000
        );
        update({
          accounts: response.getAccount || [],
          pendingDebts: (response.debtsToPay || []).filter(isPending),
          loading: false,
        });
      } catch (error) {
        update({ loading: false });
        if (isMounted) handleErrorMessage(error);
      }
    };

    const loadPendingPays = async () => {
      try {
        const { data, meta } = await withRetry(() =>
          findMyPaysAsProperty(1, PENDING_PAYS_PREVIEW, {
            condominiumId,
            status: "pending",
          })
        );
        const items = data || [];
        update({
          pendingPays: { items, total: meta?.total ?? items.length, loading: false },
        });
      } catch (error) {
        update({ pendingPays: { items: [], total: 0, loading: false } });
      }
    };

    const loadReceivableTotal = async () => {
      try {
        const { total_amount } = await withRetry(() =>
          fetchDebtsToPayAsPropertyPendingTotal({ condominiumId })
        );
        update({ receivableTotal: { value: Number(total_amount || 0), loading: false } });
      } catch (error) {
        update({ receivableTotal: { value: 0, loading: false } });
      }
    };

    const loadExpenseTypes = async () => {
      try {
        const response = await withRetry(() => fetchExpenses(condominiumId));
        update({
          expenseTypes: (response.data || []).filter(
            (type) => !HIDDEN_EXPENSE_TYPES.includes(type.id)
          ),
        });
      } catch (error) {
        update({ expenseTypes: [] });
      }
    };

    loadFinances();
    if (isManager) {
      loadPendingPays();
      loadReceivableTotal();
      loadExpenseTypes();
    }

    return () => {
      isMounted = false;
    };
  }, [condominiumId, enabled, isManager, reloadKey]);

  const totalPendingDebts = state.pendingDebts.reduce(
    (sum, debt) => sum + pendingAmountOf(debt),
    0
  );

  return { ...state, totalPendingDebts, reload };
};

export default useFinanceHubData;
